import type { ReactElement, ReactNode } from 'react';
import React, { Children, cloneElement } from 'react';
import { isNamedContainer } from '@alfa-bank/site-named-container/utils';

import type { TPersonalizationProvider, IChildrenList, IOnChildClickHandler } from './models';
import { useRouperPersonalizationCallBack } from './hooks/use-rouper-personalization-callback';

const prepareData = (children: ReactNode) =>
    Children.toArray(children).map(childItem => {
        const child = childItem as ReactElement;

        if (isNamedContainer(child) && child.props.name) {
            return {
                child,
                isPersonalContainer: true,
                personalName: child.props.name,
            };
        }

        return {
            child,
            isPersonalContainer: false,
        };
    });

export const PersonalizationProvider = ({
    activeId,
    children,
    collectorData: { personalTags = [] } = { personalTags: [] },
    id,
    isAnalitycsEnabled = false,
    limit = Infinity,
    personalization = true,
}: TPersonalizationProvider) => {
    const banditCallback = useRouperPersonalizationCallBack(isAnalitycsEnabled);

    if (!personalization) {
        return <>{children}</>;
    }

    /**
     * Хендлер клика по элементу персонализации.
     * В случае клика по кнопке (`<button />`) или ссылке (`<a />`) будет отправляться аналитика в сервис Bandit
     * При заданном значении activeId сработает бандит элемента, обернутого в контейнер, id которого соответствует значению activeId
     */
    const onChildClickHandler = ({ event, tagGroupId }: IOnChildClickHandler) => {
        const target = event.target as HTMLElement;
        // Отключена проверка, чтобы не писать длинную и некрасивую проверку
        // eslint-disable-next-line sonarjs/no-collapsible-if
        if (target.closest('a') || target.closest('button')) {
            if ((activeId && target.closest(`#${activeId}`)) || !activeId) {
                banditCallback({ tagGroupId });

                return;
            }
        }
    };

    // Подготавливаем данные
    const childrenList: IChildrenList[] = prepareData(children);

    const resultList: ReactElement[] = [];
    const notFoundAtPersonalList: IChildrenList[] = [];
    // Индекс для выбора группы персонализации
    let personCount = 0;

    // Ищем по id необходимую группу персонализации
    const personal = personalTags.find(({ tagGroupId }) => tagGroupId === id);

    // Создаём отсортированный список
    // eslint-disable-next-line sonarjs/cognitive-complexity
    childrenList.forEach(childListItem => {
        if (childListItem.isPersonalContainer && personal) {
            const { tagGroupId, tags } = personal;

            let findedItem: IChildrenList | null | undefined = null;

            // Ищем, перебирая personal-группы, и по всему списку NamedContainer-ов
            // Пока не найдём, или пока не выйдем за пределы списка personal
            while (!findedItem && personCount < tags.length) {
                findedItem = childrenList.find(
                    // eslint-disable-next-line no-loop-func
                    // eslint-disable-next-line @typescript-eslint/no-loop-func
                    listItem => listItem.personalName && tags[personCount] === listItem.personalName
                );
                personCount += 1;
            }

            if (findedItem) {
                const productId = tags[personCount - 1];
                const isTargetAction = findedItem.personalName === tags[0];

                const wrappedItem = (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                    <div
                        key={productId}
                        onClick={
                            isTargetAction
                                ? event => onChildClickHandler({ event, tagGroupId })
                                : undefined
                        }
                    >
                        {findedItem.child}
                    </div>
                );

                resultList.push(wrappedItem);

                // Сохранили элемент из начального списка.
                // нужно для кейса, когда групп в блоках больше, чем выдаёт коллектор
                if (childListItem && !tags.includes(childListItem.personalName as string)) {
                    notFoundAtPersonalList.push(childListItem);
                }
            } else if (notFoundAtPersonalList.length) {
                const item = notFoundAtPersonalList.shift();

                // Условие добавлено из-за ts, поэтому выключено из покрытия
                /* istanbul ignore next-line */
                if (item) {
                    resultList.push(item.child);
                }
            } else {
                // Когда сохранённых групп уже нет, просто выводим ноду
                resultList.push(childListItem.child);
            }
        } else {
            // Это не персонализация, просто выводим ноду
            resultList.push(childListItem.child);
        }
    });

    return (
        <>
            {resultList.slice(0, limit).map(child => {
                return cloneElement(child, {
                    ...child.props,
                });
            })}
        </>
    );
};
